import React, { FC, useState, useEffect, MouseEvent } from 'react';

import { Business } from 'models/entities/business';
import { DishCategory } from 'models/entities/dish-category';
import { OriginalDishCategory } from 'models/entities/original-dish-category';
import { Model } from './index.model';

import { useModal } from '../../services/use-modal';
import { useDnd } from 'views/services/dnd';
import { SecondaryButton } from 'views/components/buttons';
import { Dropdown } from './views/dropdown';
import { Root, Main, LeftPanel, RightPanel, Heading, List, Footer } from './index.styled';

interface Props {
  target: Business;
}

const EditDishCategories: FC<Props> = ({ target }) => {

  const modal = useModal();
  const [model, setModel] = useState(new Model({ target }));
  const dnd = useDnd();

  function close() {
    modal.close({ state: 'updated', target: model.result })
  }

  function add(category: DishCategory) {
    setModel(model.add(category));
    category.save().then(category => setModel(model.add(category, true)));
  }

  function move(category: DishCategory) {
    setModel(model.merge(category));
    category.save().then(category => setModel(model.merge(category, true)));
  }

  function remove(category: DishCategory) {
    if (model.relationship?.has(category.id!)) return;
    setModel(model.remove(category));
    category.delete().then(category => setModel(model.remove(category, true)));
  }

  function grabOriginalCategory(target: OriginalDishCategory, e: MouseEvent<HTMLLIElement>) {
    dnd.grab('dnd-list-item', { target, e });
  }

  function dropToOriginalCategories() {
    if (!dnd.state.canDrop()) return;
    if (!dnd.target || !(dnd.target instanceof DishCategory)) return;
    remove(dnd.target as DishCategory);
  }

  function grabCategory(target: DishCategory, e: MouseEvent<HTMLLIElement>) {
    dnd.grab('dnd-list-item', { target, e, label: target.original.name });
  }

  function dropToCategories(e: MouseEvent<HTMLDivElement>) {
    if (!dnd.state.canDrop()) return;
    if (!dnd.target || !(dnd.target instanceof OriginalDishCategory)) return;
    if (model.current.dishCategories.documents.find(it => it.id === dnd.target!.id)) return;
    add(model.build(dnd.target as OriginalDishCategory));
    e.stopPropagation();
    dnd.drop();
  }

  function insertAround(category: DishCategory, index: number, e: MouseEvent<HTMLLIElement>) {
    if (!dnd.state.canDrop()) return;
    if (!dnd.target) return;
    if (dnd.target === category) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    const mark = index + (e.pageY < center ? 0 : 1);
    const prev = model.categories.documents[mark - 1];
    const next = model.categories.documents[mark];
    dnd.target instanceof DishCategory ? move(dnd.target.moveBetween(prev, next)) : add(model.buildBetween(prev, next, dnd.target as OriginalDishCategory));
    e.stopPropagation();
    dnd.drop();
  }

  function watchMenuCategories() {
    if (!model.menuCategories.nextToken) return;
    model.readAllMenuCategories().then(it => setModel(it));
  }

  function watchCategories() {
    if (model.categories.nextToken) model.readAllCategories().then(it => setModel(it));
    if (!model.relationship) model.readRelationshipCollections().then(it => setModel(it));
  }

  function watchOriginalCategories() {
    if (!model.originalCategories.nextToken) return;
    model.readAllOriginalCategories().then(it => setModel(it));
  }

  useEffect(watchMenuCategories, [model.menuCategories]);
  useEffect(watchCategories, [model.categories]);
  useEffect(watchOriginalCategories, [model.originalCategories]);

  return (
    <Root>
      <Main>
        <LeftPanel>
          <Heading>
            <Dropdown list={model.menuCategories.documents} onSelect={it => setModel(model.select(it))}>
              <h1>{model.current.original.name}</h1>
            </Dropdown>
          </Heading>
          <List onMouseUp={() => dropToOriginalCategories()}>
            <ol>
              {model.originalCategoryList.map((it, i) => (
                <li key={i} onMouseDown={e => grabOriginalCategory(it, e)} className={[dnd.state.name, it === dnd.target ? 'dnd-target' : ''].join(' ')}>{it.name}</li>
              ))}
            </ol>
          </List>
        </LeftPanel>
        <RightPanel>
          <hgroup>
            <h1>{model.target.name}</h1>
            <p>{model.target.location}</p>
          </hgroup>
          <List onMouseUp={e => dropToCategories(e)} onMouseMove={e => dnd.enableScroll(e)} onMouseLeave={() => dnd.disableScroll()}>
            <ol>
              {model.categories.documents.map((it, i) => (
                <li key={i} onMouseDown={e => grabCategory(it, e)} onMouseMove={e => dnd.mark(e)} onMouseLeave={() => dnd.unmark()} onMouseUp={e => insertAround(it, i, e)} className={[dnd.state.name, it === dnd.target ? 'dnd-target' : '', model.relationship?.has(it.id!) ? 'disabled' : ''].join(' ')}>{it.original.name}</li>
              ))}
            </ol>
          </List>
        </RightPanel>
      </Main>
      <Footer>
        <SecondaryButton onClick={() => close()}>Done</SecondaryButton>
      </Footer>
    </Root>
  );

};

export { EditDishCategories };
